import {
  AfterViewInit,
  Component,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from './auth/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { delay, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { SettingsService } from './common/settings.service';
import { DossierNotificationService } from './services/notification/dossier-notification.service';
import { AdornmentsService, AppFocusArea } from './adornments.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements AfterViewInit {
  title = 'avg-frontend';
  auth: AuthService;

  languages = ['de', 'en', 'fr'];

  isEmbed = false;

  focusAreas: AppFocusArea[] = [];

  constructor(
    private router: Router,
    auth: AuthService,
    settings: SettingsService,
    public translator: TranslateService,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private dossierNotificationService: DossierNotificationService,
    private adornments: AdornmentsService
  ) {
    this.auth = auth;
    if (
      window.location.pathname.includes('embed') ||
      window.location.search.includes('embed=true')
    ) {
      this.isEmbed = true;
    }

    this.auth.jwtToken$
      .pipe(
        map((t) => Boolean(t)),
        delay(50),
        distinctUntilChanged()
      )
      .subscribe((tk) => {
        if (tk) {
          settings.initializeUserSettings();
        } else {
          settings.resetSettings();
        }
      });

    this.handleRouteChanges();

    this.dossierNotificationService.notifyInDossier();
    this.dossierNotificationService.notifyInDashboard();

    this.adornments.focusAreas.subscribe((areas) => {
      this.focusAreas = areas;
    });
  }

  get realm(): string {
    return this.activatedRoute?.snapshot?.firstChild?.paramMap?.get('realm');
  }

  ngAfterViewInit(): void {
    window.setTimeout(
      () =>
        document.querySelector('#main-splash-screen').classList.add('loaded'),
      1000
    );
  }

  focusOnArea(event: PointerEvent, area: AppFocusArea) {
    event.preventDefault();
    event.stopPropagation();

    // Find the first focusable element within the area
    const focusableElement = area.element.querySelector(
      'input, button, select, textarea, a, area[href], iframe, [tabindex]:not([tabindex="-1"]), *[role="button"]'
    ) as HTMLElement;

    if (focusableElement) {
      focusableElement.focus();
      if (area.focusCallback) {
        area.focusCallback();
      }
    }
  }

  private handleRouteChanges() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data['title']) {
              return child.snapshot.data['title'];
            } else {
              return null;
            }
          }
          return null;
        })
      )
      .subscribe((data) => {
        if (data) {
          this.titleService.setTitle(
            `${this.translator.instant(data)} | Silberfluss`
          );
        }
      });
  }
}
